import React, { FC, useCallback, useEffect, useRef } from 'react'
import { useRouter } from '@moonpig/web-core-routing'
import { useSearchStore } from '../../../store/SearchStore'
import { useFindLocaleText } from '../../../text-localisation'
import { NextButton, PreviousButton, PageButtons } from './components'
import { createPaginationItems } from './createPaginationItems'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { PaginatedGalleryRoute } from './types'
import { PageNavigationSource } from '../../../store/slices/products/types'
import { scrollToTopOfPageContainer } from './scrollToTopOfPageContainer'
import {
  getPageNumberFromOffsetParam,
  isPaginatedGalleryRouteName,
} from './utils'
import {
  consumeSkipNextHistoryPageSync,
  getHistoryPageSyncDestinationKey,
} from '../../../store/helpers/skipNextHistoryPageSync'

const StyledPaginationNav = styled.nav`
  ${s({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 3,
    py: 6,
    px: { xs: 5, md: 0 },
    mb: 6,
    borderTop: 1,
    borderBottom: 1,
    borderColor: 'colorBorder03',
  })}
`

export const PageNavigation: FC<{
  totalCount: number
  productGridRef: React.RefObject<HTMLDivElement>
}> = ({ totalCount, productGridRef }) => {
  const router = useRouter()
  const localiseText = useFindLocaleText()
  const currentPage = useSearchStore(store => store.topPage)
  const loadPage = useSearchStore(store => store.loadPage)
  const galleryPageSize = useSearchStore(store => store.galleryPageSize)

  const totalPages = Math.ceil(totalCount / galleryPageSize)

  const topPageRef = useRef(currentPage)
  topPageRef.current = currentPage

  const skipNextHistoryPageSyncRef = useRef(false)

  useEffect(() => {
    return router.onRouteChangeComplete(() => {
      if (skipNextHistoryPageSyncRef.current) {
        skipNextHistoryPageSyncRef.current = false
        return
      }

      const route = router.getCurrentRoute<PaginatedGalleryRoute>()
      const routeParams = route.params as unknown as Record<string, string>
      if (
        consumeSkipNextHistoryPageSync(
          getHistoryPageSyncDestinationKey(route.name, routeParams),
        )
      ) {
        return
      }

      const offset = routeParams.offset
      if (!isPaginatedGalleryRouteName(route.name)) {
        return
      }

      const pageFromUrl = getPageNumberFromOffsetParam(offset, galleryPageSize)

      if (pageFromUrl !== topPageRef.current) {
        scrollToTopOfPageContainer(productGridRef)
        loadPage(pageFromUrl, PageNavigationSource.History)
      }
    })
  }, [router, loadPage, productGridRef, galleryPageSize])

  const getPageHref = useCallback(
    (targetPage: number) => {
      const { name, params } = router.getCurrentRoute<PaginatedGalleryRoute>()
      const offset =
        targetPage <= 1 ? undefined : (targetPage - 1) * galleryPageSize

      return router.build(name, {
        ...params,
        offset,
      }).as
    },
    [router, galleryPageSize],
  )

  const handlePageSelect = useCallback(
    async (
      event: React.MouseEvent<HTMLElement>,
      page: number,
      source: PageNavigationSource,
    ) => {
      event.preventDefault()
      scrollToTopOfPageContainer(productGridRef)
      await loadPage(page, source)
      topPageRef.current = page
      skipNextHistoryPageSyncRef.current = true
      const { name, params } = router.getCurrentRoute<PaginatedGalleryRoute>()
      const offset = page <= 1 ? undefined : (page - 1) * galleryPageSize
      router.push({
        name,
        params: {
          ...params,
          offset,
          productId: undefined,
        },
        shallow: true,
      })
    },
    [loadPage, productGridRef, router, galleryPageSize],
  )

  if (totalPages < 1) {
    return null
  }

  const items = createPaginationItems({ currentPage, totalPages })
  const hasPrevious = currentPage > 1
  const hasNext = currentPage < totalPages

  return (
    <StyledPaginationNav
      aria-label={localiseText('find.pagination_nav_region')}
    >
      <PreviousButton
        disabled={!hasPrevious}
        currentPage={currentPage}
        onClick={(event, page) =>
          handlePageSelect(event, page, PageNavigationSource.Previous)
        }
        getPageHref={getPageHref}
      />
      <PageButtons
        items={items}
        currentPage={currentPage}
        onClick={(event, page) =>
          handlePageSelect(event, page, PageNavigationSource.Number)
        }
        getPageHref={getPageHref}
      />
      <NextButton
        disabled={!hasNext}
        currentPage={currentPage}
        onClick={(event, page) =>
          handlePageSelect(event, page, PageNavigationSource.Next)
        }
        getPageHref={getPageHref}
      />
    </StyledPaginationNav>
  )
}
